import * as React from 'react';
import { toast } from 'sonner';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { DataTable } from '../../../design-system/components/DataTable';
import { EmptyState } from '../../../design-system/components/EmptyState';
import { ErrorState } from '../../../design-system/components/ErrorState';
import { NoAccessState } from '../../../design-system/components/NoAccessState';
import { Plus, Camera } from 'lucide-react';
import { useApi } from '../../../design-system/useApi';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { formatDateTime } from '../../../design-system/format';
import { VISUAL, statusPill, statusDot } from '../../../design-system/visual';
import { monitoringApi } from '../api';
import { IssueTokenDialog } from '../components/IssueTokenDialog';
import { AgentStatusPill } from '../components/AgentStatusPill';
import { agentReleasesApi, type AgentRelease } from '../../agent-releases/api';
import type { MonitoringSettings, MonitoringAgentToken } from '../types';
import type { EmployeeWithUser } from '../../hr/types';

/** True when `seen` is a semver strictly below `latest`. Unparseable → false
 * (never nag on data we can't compare). */
function agentOutdated(seen: string | null, latest: string | null): boolean {
  if (!seen || !latest) return false;
  const a = seen.split('.').map(Number);
  const b = latest.split('.').map(Number);
  if (a.some(Number.isNaN) || b.some(Number.isNaN)) return false;
  for (let i = 0; i < 3; i++) {
    const d = (b[i] ?? 0) - (a[i] ?? 0);
    if (d !== 0) return d > 0;
  }
  return false;
}

export function MonitoringSettingsPage() {
  const settingsQ = useApi<{ settings: MonitoringSettings }>('/api/monitoring/settings');
  const tokensQ   = useApi<{ tokens: MonitoringAgentToken[] }>('/api/monitoring/agent-tokens');
  const empQ      = useApi<{ employees: EmployeeWithUser[] }>('/api/hr/employees');
  const [release, setRelease] = React.useState<AgentRelease | null>(null);
  React.useEffect(() => {
    agentReleasesApi.getTenant().then(setRelease).catch(() => { /* silent — banner just won't appear */ });
  }, []);

  const confirm = useConfirm();
  const [form, setForm] = React.useState<MonitoringSettings | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [issueOpen, setIssueOpen] = React.useState(false);

  React.useEffect(() => { if (settingsQ.data?.settings) setForm(settingsQ.data.settings); }, [settingsQ.data]);

  const employees = empQ.data?.employees ?? [];
  const employeesById: Record<string, EmployeeWithUser> = {};
  for (const e of employees) employeesById[e.id] = e;

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!form) return;
    setSaving(true);
    try {
      await monitoringApi.updateSettings(form);
      toast.success('Settings saved');
      settingsQ.reload();
    } catch (err: any) {
      toast.error('Save failed', { description: err.message });
    } finally { setSaving(false); }
  }

  async function revoke(id: string) {
    if (!(await confirm({ title: 'Revoke this agent token?', description: 'The agent will lose access.', destructive: true, confirmLabel: 'Revoke' }))) return;
    try {
      await monitoringApi.revokeAgentToken(id);
      toast.success('Token revoked');
      tokensQ.reload();
    } catch (err: any) { toast.error('Revoke failed', { description: err.message }); }
  }

  // Settings + tokens are owner/manager-only on the server. Employees landing
  // here used to see an INFINITE spinner (settings 403 → form never set).
  // Show a calm no-access card for the expected 403, a real error otherwise.
  if (settingsQ.status === 403) {
    return (
      <div>
        <PageHeader
          eyebrow="Work / Monitoring / Settings"
          title="Capture settings"
          description="Privacy, capture interval, and per-employee agent tokens."
        />
        <div className="px-6 sm:px-8 lg:px-10 pb-12 max-w-4xl">
          <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <NoAccessState description="Monitoring settings are managed by workspace owners and monitoring managers. Your own capture log lives under Monitoring." />
          </section>
        </div>
      </div>
    );
  }
  if (settingsQ.error) {
    return (
      <div>
        <PageHeader
          eyebrow="Work / Monitoring / Settings"
          title="Capture settings"
          description="Privacy, capture interval, and per-employee agent tokens."
        />
        <div className="px-6 sm:px-8 lg:px-10 pb-12 max-w-4xl">
          <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <ErrorState title="Couldn't load monitoring settings" description={settingsQ.error} onRetry={settingsQ.reload} />
          </section>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow="Work / Monitoring / Settings"
        title="Capture settings"
        description="Privacy, capture interval, and per-employee agent tokens."
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12 max-w-4xl space-y-6">
        <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
          <h3 className={[VISUAL.dashSection, 'mb-5'].join(' ')}>Capture defaults</h3>
          {!form && <LoadingState variant="inline" />}
          {form && (
            <form onSubmit={save} className="space-y-5">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.monitoring_working_hours_only}
                  onChange={(e) => setForm({ ...form, monitoring_working_hours_only: e.target.checked })}
                  className="mt-1 h-4 w-4 rounded border-border accent-saffron"
                />
                <span className="flex-1">
                  <span className="text-[13.5px] font-semibold text-foreground block">Working hours only</span>
                  <span className="text-[12.5px] text-muted-foreground block mt-0.5">Agent suppresses uploads outside the employee's scheduled clock-in/out window.</span>
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.monitoring_blur_enabled}
                  onChange={(e) => setForm({ ...form, monitoring_blur_enabled: e.target.checked })}
                  className="mt-1 h-4 w-4 rounded border-border accent-saffron"
                />
                <span className="flex-1">
                  <span className="text-[13.5px] font-semibold text-foreground block">Blur enabled</span>
                  <span className="text-[12.5px] text-muted-foreground block mt-0.5">Agent blurs screen content client-side before uploading.</span>
                </span>
              </label>
              <div className="grid gap-1.5 max-w-xs">
                <Label>Capture interval (seconds, 30–3600)</Label>
                <Input type="number" min={30} max={3600} value={form.monitoring_capture_interval_seconds}
                  onChange={(e) => setForm({ ...form, monitoring_capture_interval_seconds: Number(e.target.value) })} />
              </div>
              <div className="grid gap-1.5 max-w-xs">
                <Label>Screenshot retention (days, 7–730)</Label>
                <Input type="number" min={7} max={730} placeholder="Keep forever"
                  value={form.monitoring_retention_days ?? ''}
                  onChange={(e) => setForm({
                    ...form,
                    monitoring_retention_days: e.target.value === '' ? null : Number(e.target.value),
                  })} />
                <span className="text-[12.5px] text-muted-foreground">Screenshots older than this are deleted automatically. Leave empty to keep forever.</span>
              </div>
              <div className="flex justify-end pt-2 border-t border-border/40">
                <Button type="submit" disabled={saving} className="rounded-full h-10 px-5 text-[13px] font-semibold mt-4">
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </form>
          )}
        </section>

        {release?.version && (tokensQ.data?.tokens ?? []).some((t) => !t.revoked_at && agentOutdated(t.last_seen_version, release.version)) && (
          <section className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-[13px] text-foreground">
            <p className="font-semibold">Some agents are running an outdated version — latest is {release.version}.</p>
            <p className="text-muted-foreground mt-1">
              The desktop agent doesn't update itself yet: download the latest installer and run it on the employee's machine
              (their token keeps working, no re-setup needed).
              {' '}
              {release.windows_url && <a className="underline font-medium" href={release.windows_url}>Windows installer</a>}
              {release.windows_url && release.macos_url && ' · '}
              {release.macos_url && <a className="underline font-medium" href={release.macos_url}>macOS installer</a>}
            </p>
          </section>
        )}

        <section>
          <div className="flex items-baseline justify-between mb-3">
            <h3 className={VISUAL.dashSection}>Agent tokens</h3>
            <Button onClick={() => setIssueOpen(true)} className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5">
              <Plus className="h-3.5 w-3.5" />
              Issue token
            </Button>
          </div>
          <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <DataTable<MonitoringAgentToken>
              loading={tokensQ.loading}
              rows={tokensQ.data?.tokens ?? []}
              rowKey={(r) => r.id}
              columns={[
                { key: 'employee', header: 'Employee', cell: (r) => employeesById[r.employee_id]?.name ?? r.employee_id.slice(0, 8) },
                { key: 'label',    header: 'Label',    cell: (r) => r.agent_label ?? '—', className: 'text-muted-foreground' },
                { key: 'created',  header: 'Created',  cell: (r) => formatDateTime(r.created_at), className: 'text-muted-foreground' },
                { key: 'last',     header: 'Last used', cell: (r) => r.last_used_at ? formatDateTime(r.last_used_at) : '—', className: 'text-muted-foreground' },
                { key: 'heartbeat', header: 'Last heartbeat', cell: (r) => r.last_heartbeat_at ? formatDateTime(r.last_heartbeat_at) : '—', className: 'text-muted-foreground' },
                { key: 'version',  header: 'Agent version', cell: (r) => (
                  <span className="tabular-nums">
                    {r.last_seen_version ?? '—'}
                    {!r.revoked_at && agentOutdated(r.last_seen_version, release?.version ?? null) && (
                      <span className={[statusPill('warning'), 'ml-2'].join(' ')}>update available</span>
                    )}
                  </span>
                ), className: 'text-muted-foreground' },
                { key: 'status',   header: 'Status',   cell: (r) => r.revoked_at
                  ? <span className={statusPill('destructive')}><span className={statusDot('destructive')} />Revoked</span>
                  : <AgentStatusPill status={r.status ?? 'never'} lastHeartbeatAt={r.last_heartbeat_at} /> },
                { key: 'actions',  header: '',         cell: (r) => r.revoked_at
                  ? null
                  : <Button size="sm" variant="ghost" className="rounded-full h-8 text-muted-foreground hover:text-destructive" onClick={() => revoke(r.id)}>Revoke</Button> },
              ]}
              emptyState={
                tokensQ.error
                  ? <ErrorState title="Couldn't load agent tokens" description={tokensQ.error} onRetry={tokensQ.reload} />
                  : <EmptyState icon={<Camera />} title="No agent tokens" description="Issue a token to set up the desktop agent for an employee." />
              }
            />
          </div>
        </section>
      </div>

      <IssueTokenDialog open={issueOpen} onOpenChange={setIssueOpen} employees={employees} onIssued={tokensQ.reload} />
    </div>
  );
}
